import type { Shadows, ThemeOptions } from '@mui/material'
import { avatarClasses } from '@mui/material'

import { desktopStyles } from '@salsita-ai/platform-project-sdk/ui'

const shadows = [
  'none',
  `
    0 2px 8px -1px hsl(from black h s l / 10%),
    0 1px 4px -2px hsl(from black h s l / 10%)
  `,
  ...Array(23).fill('none'),
] as Shadows

const themeOptions = {
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
        },
      },
    },

    MuiListItemAvatar: {
      styleOverrides: {
        root: ({ theme: { palette } }) => ({
          minWidth: 52,

          [`.${avatarClasses.root}`]: {
            background: palette.action.focus,
            borderRadius: 16,
          },
          [`.${avatarClasses.img}`]: {
            blockSize: 24,
            inlineSize: 24,
          },
        }),
      },
    },

    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },

    // Sidebar
    PhxDesktopSidebar: {
      defaultProps: {
        filter: `
          drop-shadow(0 2px 8px hsl(from black h s l / 5%))
          drop-shadow(0 1px 4px hsl(from black h s l / 5%))
        `, // elevation[1] equivalent
      },
    },

    // @ts-expect-error type definition issue
    PhxDesktopBackButton: {
      styleOverrides: {
        button: {
          display: 'none',
        },
      },
    },

    PhxPropertyContainerItem: {
      styleOverrides: {
        content: ({ theme: { spacing } }) => ({
          marginBlockStart: spacing(2),
        }),
      },
    },

    // Branding
    PhxDesktopBrandingHeader: {
      defaultProps: {
        imageAlt: 'AWR Solutions logo',
        imageHeight: 32,
        imageUrl: '/images/logo-awr.avif',
      },
    },
    PhxDesktopBrandingPoweredBy: {
      defaultProps: {
        visible: false,
      },
    },
    PhxMobileBrandingHeader: {
      defaultProps: {
        imageAlt: 'AWR Solutions logo',
        imageHeight: 24,
        imageUrl: '/images/logo-awr.avif',
      },
    },
    PhxMobileBrandingPoweredBy: {
      defaultProps: {
        visible: false,
      },
    },

    // Scene buttons
    PhxSceneFab: {
      defaultProps: {
        size: 'medium',
      },
      styleOverrides: {
        fab: {
          borderRadius: 16,
        },
      },
    },
    PhxSketchingEditSceneButton: {
      styleOverrides: {
        button: {
          borderRadius: 'calc(infinity * 1px)',
        },
      },
    },

    // Hotspots
    PhxSpotlight: {
      defaultProps: {
        color: '#0795A5',
        cursor: 'crosshair',
      },
    },
    PhxDragHotspot: {
      defaultProps: {
        color: '#0795A5',
      },
    },
    PhxRecipeHotspot: {
      defaultProps: {
        color: '#0795A5',
      },
    },

    // Selectors
    PhxListSelectorItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          minBlockSize: 0,
        },
        formControlLabel: ({ theme: { spacing } }) => ({
          paddingBlock: spacing(1),
        }),
      },
    },

    PhxToggleSelectorRoot: {
      defaultProps: {
        columnCount: 'auto-fit',
      },
    },
    PhxToggleSelectorItem: {
      styleOverrides: {
        button: {
          paddingBlock: 8,

          [`&:is(*, #chucknorris)`]: {
            borderRadius: 8,
          },
        },
      },
    },

    PhxSwitchSelector: {
      styleOverrides: {
        title: ({ theme: { typography } }) => ({
          // We need to increase the specificity for these.
          '&[class]': {
            ...typography.body1,
          },
        }),
        description: ({ theme: { palette, typography } }) => ({
          ...typography.caption,
          color: palette.text.secondary,
        }),
      },
    },
  },

  // ---------------------------------------------------------------------------
  palette: {
    background: {
      scene: '#F2F3F4',
      default: '#FBFBFB',
    },
    primary: {
      main: '#131E48',
      dark: '#081235',
      light: '#2F3E78',
    },
    secondary: {
      main: '#EB9B2D',
      dark: '#C37A16',
      light: '#EEAF57',
    },
  },

  shadows,

  // ---------------------------------------------------------------------------
  shape: {
    borderRadius: 16,
  },

  // ---------------------------------------------------------------------------
  typography: {
    fontFamily: '"Geist Variable", sans-serif',
    h4: {
      ...desktopStyles({
        fontWeight: 500,
      }),
    },
    h5: {
      ...desktopStyles({
        fontWeight: 500,
      }),
    },
    overline: {
      ...desktopStyles({
        letterSpacing: '0.083333em',
      }),
    },
  },
} satisfies ThemeOptions

export const theme = themeOptions
